// Built-in recipe library for the Meal Planner.
// Each recipe has ingredients that expand into checklist items when the user
// generates a shopping list. Everything ships offline — no external API.

export const BUILTIN_RECIPES = [
  {
    id: "recipe-spaghetti-bolognese",
    name: "Spaghetti Bolognese",
    tagline: "Classic Italian weeknight comfort.",
    servings: 4,
    time_min: 45,
    tags: ["dinner", "italian", "pasta"],
    ingredients: [
      { text: "Spaghetti", qty: "500g", dept: "Pantry" },
      { text: "Ground beef", qty: "500g", dept: "Meat & Seafood" },
      { text: "Yellow onion", qty: "1", dept: "Produce" },
      { text: "Garlic cloves", qty: "3", dept: "Produce" },
      { text: "Crushed tomatoes", qty: "1 can", dept: "Pantry" },
      { text: "Tomato paste", qty: "2 tbsp", dept: "Pantry" },
      { text: "Parmesan cheese", qty: "50g", dept: "Dairy" },
      { text: "Olive oil", qty: "2 tbsp", dept: "Pantry" },
      { text: "Salt & pepper", qty: "to taste", dept: "Pantry" },
    ],
    steps: [
      "Sauté chopped onion and garlic in olive oil until soft.",
      "Add ground beef, brown thoroughly.",
      "Stir in tomato paste, then crushed tomatoes. Simmer 25 min.",
      "Cook spaghetti to package directions, drain.",
      "Serve sauce over pasta, top with grated Parmesan.",
    ],
  },
  {
    id: "recipe-chicken-stir-fry",
    name: "Chicken Stir-Fry",
    tagline: "Fast, veggie-packed, dinner in 20 minutes.",
    servings: 4,
    time_min: 25,
    tags: ["dinner", "asian", "quick"],
    ingredients: [
      { text: "Chicken breast", qty: "500g", dept: "Meat & Seafood" },
      { text: "Broccoli florets", qty: "2 cups", dept: "Produce" },
      { text: "Bell peppers", qty: "2", dept: "Produce" },
      { text: "Carrots", qty: "2", dept: "Produce" },
      { text: "Soy sauce", qty: "3 tbsp", dept: "Pantry" },
      { text: "Fresh ginger", qty: "1 tbsp", dept: "Produce" },
      { text: "Garlic cloves", qty: "3", dept: "Produce" },
      { text: "Jasmine rice", qty: "2 cups", dept: "Pantry" },
      { text: "Sesame oil", qty: "1 tbsp", dept: "Pantry" },
    ],
    steps: [
      "Cook rice per package.",
      "Slice chicken thin. Sear in hot pan.",
      "Add vegetables, ginger, garlic. Stir-fry 5 min.",
      "Stir in soy sauce and sesame oil.",
      "Serve over rice.",
    ],
  },
  {
    id: "recipe-taco-tuesday",
    name: "Taco Tuesday",
    tagline: "Family-style ground beef tacos.",
    servings: 4,
    time_min: 30,
    tags: ["dinner", "mexican", "family"],
    ingredients: [
      { text: "Ground beef", qty: "500g", dept: "Meat & Seafood" },
      { text: "Taco shells", qty: "12", dept: "Pantry" },
      { text: "Taco seasoning", qty: "1 packet", dept: "Pantry" },
      { text: "Lettuce", qty: "1 head", dept: "Produce" },
      { text: "Tomatoes", qty: "2", dept: "Produce" },
      { text: "Shredded cheese", qty: "200g", dept: "Dairy" },
      { text: "Sour cream", qty: "1 cup", dept: "Dairy" },
      { text: "Salsa", qty: "1 jar", dept: "Pantry" },
    ],
    steps: [
      "Brown beef. Drain fat.",
      "Add seasoning + 1/4 cup water. Simmer 5 min.",
      "Warm taco shells.",
      "Set out toppings. Let everyone build their own.",
    ],
  },
  {
    id: "recipe-oatmeal-breakfast",
    name: "Overnight Oats",
    tagline: "Prep the night before — grab and go breakfast.",
    servings: 1,
    time_min: 5,
    tags: ["breakfast", "quick", "meal-prep"],
    ingredients: [
      { text: "Rolled oats", qty: "1/2 cup", dept: "Pantry" },
      { text: "Milk (or oat milk)", qty: "1 cup", dept: "Dairy" },
      { text: "Greek yogurt", qty: "1/4 cup", dept: "Dairy" },
      { text: "Honey", qty: "1 tbsp", dept: "Pantry" },
      { text: "Fresh berries", qty: "1/2 cup", dept: "Produce" },
      { text: "Chia seeds", qty: "1 tbsp", dept: "Pantry" },
    ],
    steps: [
      "Combine oats, milk, yogurt, honey, and chia seeds in a jar.",
      "Stir well, seal, refrigerate 6+ hours.",
      "Top with fresh berries in the morning.",
    ],
  },
  {
    id: "recipe-grilled-salmon",
    name: "Grilled Salmon & Veg",
    tagline: "Sheet-pan dinner with lemon-herb salmon.",
    servings: 4,
    time_min: 30,
    tags: ["dinner", "healthy", "seafood"],
    ingredients: [
      { text: "Salmon fillets", qty: "4", dept: "Meat & Seafood" },
      { text: "Asparagus", qty: "1 bunch", dept: "Produce" },
      { text: "Cherry tomatoes", qty: "1 pint", dept: "Produce" },
      { text: "Lemon", qty: "2", dept: "Produce" },
      { text: "Olive oil", qty: "3 tbsp", dept: "Pantry" },
      { text: "Fresh dill", qty: "1 bunch", dept: "Produce" },
      { text: "Salt & pepper", qty: "to taste", dept: "Pantry" },
    ],
    steps: [
      "Preheat oven to 200°C / 400°F.",
      "Arrange salmon, asparagus, tomatoes on sheet pan.",
      "Drizzle olive oil, squeeze lemon, sprinkle dill, salt, pepper.",
      "Roast 15 min until salmon flakes.",
    ],
  },
  {
    id: "recipe-veggie-buddha-bowl",
    name: "Veggie Buddha Bowl",
    tagline: "Colorful vegetarian bowl with tahini drizzle.",
    servings: 2,
    time_min: 35,
    tags: ["lunch", "vegetarian", "healthy"],
    ingredients: [
      { text: "Quinoa", qty: "1 cup", dept: "Pantry" },
      { text: "Sweet potato", qty: "1 large", dept: "Produce" },
      { text: "Chickpeas", qty: "1 can", dept: "Pantry" },
      { text: "Baby spinach", qty: "2 cups", dept: "Produce" },
      { text: "Avocado", qty: "1", dept: "Produce" },
      { text: "Tahini", qty: "3 tbsp", dept: "Pantry" },
      { text: "Lemon", qty: "1", dept: "Produce" },
    ],
    steps: [
      "Cook quinoa. Roast diced sweet potato at 200°C for 25 min.",
      "Drain chickpeas, toss with olive oil, roast last 10 min with potatoes.",
      "Whisk tahini with lemon juice + water until pourable.",
      "Assemble: quinoa, sweet potato, chickpeas, spinach, avocado.",
      "Drizzle with tahini sauce.",
    ],
  },
  {
    id: "recipe-chicken-caesar",
    name: "Chicken Caesar Salad",
    tagline: "Crisp romaine with grilled chicken.",
    servings: 2,
    time_min: 20,
    tags: ["lunch", "salad"],
    ingredients: [
      { text: "Chicken breasts", qty: "2", dept: "Meat & Seafood" },
      { text: "Romaine lettuce", qty: "1 head", dept: "Produce" },
      { text: "Caesar dressing", qty: "1/2 cup", dept: "Pantry" },
      { text: "Croutons", qty: "1 cup", dept: "Pantry" },
      { text: "Parmesan cheese", qty: "50g", dept: "Dairy" },
      { text: "Lemon", qty: "1", dept: "Produce" },
    ],
    steps: [
      "Season and grill chicken 6 min/side.",
      "Chop romaine, toss with dressing.",
      "Top with sliced chicken, croutons, Parmesan, lemon wedge.",
    ],
  },
  {
    id: "recipe-pancakes-weekend",
    name: "Weekend Pancakes",
    tagline: "Fluffy stack for lazy mornings.",
    servings: 4,
    time_min: 25,
    tags: ["breakfast", "family", "weekend"],
    ingredients: [
      { text: "All-purpose flour", qty: "2 cups", dept: "Pantry" },
      { text: "Baking powder", qty: "2 tsp", dept: "Pantry" },
      { text: "Sugar", qty: "3 tbsp", dept: "Pantry" },
      { text: "Milk", qty: "1.5 cups", dept: "Dairy" },
      { text: "Eggs", qty: "2", dept: "Dairy" },
      { text: "Butter", qty: "3 tbsp", dept: "Dairy" },
      { text: "Maple syrup", qty: "for serving", dept: "Pantry" },
    ],
    steps: [
      "Whisk dry ingredients.",
      "Beat wet ingredients. Combine — don't over-mix.",
      "Cook on medium-hot griddle 2 min/side.",
      "Serve with butter and maple syrup.",
    ],
  },
  {
    id: "recipe-chili-con-carne",
    name: "Chili Con Carne",
    tagline: "Batch-cook winter warmer, freezes well.",
    servings: 6,
    time_min: 60,
    tags: ["dinner", "batch", "hearty"],
    ingredients: [
      { text: "Ground beef", qty: "700g", dept: "Meat & Seafood" },
      { text: "Kidney beans", qty: "2 cans", dept: "Pantry" },
      { text: "Diced tomatoes", qty: "2 cans", dept: "Pantry" },
      { text: "Yellow onion", qty: "1", dept: "Produce" },
      { text: "Bell peppers", qty: "2", dept: "Produce" },
      { text: "Chili powder", qty: "2 tbsp", dept: "Pantry" },
      { text: "Cumin", qty: "1 tbsp", dept: "Pantry" },
      { text: "Garlic cloves", qty: "3", dept: "Produce" },
    ],
    steps: [
      "Sauté onion, garlic, peppers.",
      "Brown beef.",
      "Add tomatoes, drained beans, spices.",
      "Simmer covered 40+ min.",
      "Serve with rice, cornbread, or crackers.",
    ],
  },
  {
    id: "recipe-margherita-pizza",
    name: "Margherita Pizza",
    tagline: "Simple, fresh, unbeatable.",
    servings: 4,
    time_min: 30,
    tags: ["dinner", "italian", "family"],
    ingredients: [
      { text: "Pizza dough", qty: "1 lb", dept: "Bakery" },
      { text: "San Marzano tomatoes", qty: "1 can", dept: "Pantry" },
      { text: "Fresh mozzarella", qty: "250g", dept: "Dairy" },
      { text: "Fresh basil", qty: "1 bunch", dept: "Produce" },
      { text: "Olive oil", qty: "2 tbsp", dept: "Pantry" },
      { text: "Salt", qty: "to taste", dept: "Pantry" },
    ],
    steps: [
      "Preheat oven / pizza stone to 250°C / 480°F.",
      "Stretch dough. Crush tomatoes with hands, spread thin.",
      "Tear mozzarella over top.",
      "Bake 8-10 min until crust is golden.",
      "Top with fresh basil and olive oil.",
    ],
  },
  {
    id: "recipe-thai-curry",
    name: "Thai Green Curry",
    tagline: "Coconut, ginger, and lemongrass.",
    servings: 4,
    time_min: 35,
    tags: ["dinner", "asian", "spicy"],
    ingredients: [
      { text: "Chicken thighs", qty: "500g", dept: "Meat & Seafood" },
      { text: "Green curry paste", qty: "3 tbsp", dept: "Pantry" },
      { text: "Coconut milk", qty: "1 can", dept: "Pantry" },
      { text: "Fish sauce", qty: "2 tbsp", dept: "Pantry" },
      { text: "Thai basil", qty: "1 bunch", dept: "Produce" },
      { text: "Lime", qty: "2", dept: "Produce" },
      { text: "Jasmine rice", qty: "2 cups", dept: "Pantry" },
      { text: "Snap peas", qty: "200g", dept: "Produce" },
    ],
    steps: [
      "Cook rice.",
      "Fry curry paste in a splash of coconut milk 2 min.",
      "Add chicken, brown.",
      "Pour in remaining coconut milk, fish sauce. Simmer 15 min.",
      "Add snap peas last 3 min. Finish with basil and lime.",
    ],
  },
  {
    id: "recipe-avocado-toast",
    name: "Avocado Toast",
    tagline: "Brunch classic in 5 minutes.",
    servings: 2,
    time_min: 10,
    tags: ["breakfast", "quick"],
    ingredients: [
      { text: "Sourdough bread", qty: "4 slices", dept: "Bakery" },
      { text: "Avocado", qty: "2", dept: "Produce" },
      { text: "Eggs", qty: "2", dept: "Dairy" },
      { text: "Lemon", qty: "1", dept: "Produce" },
      { text: "Chili flakes", qty: "pinch", dept: "Pantry" },
      { text: "Salt & pepper", qty: "to taste", dept: "Pantry" },
    ],
    steps: [
      "Toast bread. Mash avocado with lemon, salt, pepper.",
      "Fry or poach eggs.",
      "Spread avocado, top with egg, sprinkle chili flakes.",
    ],
  },
  {
    id: "recipe-pot-roast",
    name: "Slow-Cooker Pot Roast",
    tagline: "Set-and-forget Sunday dinner.",
    servings: 6,
    time_min: 480,
    tags: ["dinner", "slow-cooker", "family"],
    ingredients: [
      { text: "Beef chuck roast", qty: "1.5kg", dept: "Meat & Seafood" },
      { text: "Baby potatoes", qty: "1kg", dept: "Produce" },
      { text: "Carrots", qty: "6", dept: "Produce" },
      { text: "Yellow onion", qty: "1 large", dept: "Produce" },
      { text: "Beef broth", qty: "500ml", dept: "Pantry" },
      { text: "Worcestershire sauce", qty: "2 tbsp", dept: "Pantry" },
      { text: "Fresh thyme", qty: "few sprigs", dept: "Produce" },
    ],
    steps: [
      "Sear roast on all sides in hot pan.",
      "Transfer to slow cooker with vegetables.",
      "Pour broth, Worcestershire, add thyme.",
      "Cook LOW 8 hours or HIGH 4 hours.",
    ],
  },
  {
    id: "recipe-greek-salad",
    name: "Greek Salad",
    tagline: "Bright, briny, no-cook lunch.",
    servings: 4,
    time_min: 15,
    tags: ["lunch", "salad", "vegetarian"],
    ingredients: [
      { text: "Cucumber", qty: "1 large", dept: "Produce" },
      { text: "Tomatoes", qty: "4", dept: "Produce" },
      { text: "Red onion", qty: "1/2", dept: "Produce" },
      { text: "Kalamata olives", qty: "1/2 cup", dept: "Pantry" },
      { text: "Feta cheese", qty: "200g", dept: "Dairy" },
      { text: "Olive oil", qty: "3 tbsp", dept: "Pantry" },
      { text: "Red wine vinegar", qty: "1 tbsp", dept: "Pantry" },
      { text: "Fresh oregano", qty: "1 tsp", dept: "Produce" },
    ],
    steps: [
      "Chop cucumber, tomatoes, red onion.",
      "Toss with olives.",
      "Drizzle oil + vinegar, sprinkle oregano.",
      "Top with feta block (Greek style, not crumbled).",
    ],
  },
  {
    id: "recipe-shrimp-scampi",
    name: "Shrimp Scampi",
    tagline: "Buttery garlic shrimp over linguine.",
    servings: 4,
    time_min: 25,
    tags: ["dinner", "italian", "seafood"],
    ingredients: [
      { text: "Large shrimp", qty: "500g", dept: "Meat & Seafood" },
      { text: "Linguine", qty: "400g", dept: "Pantry" },
      { text: "Butter", qty: "60g", dept: "Dairy" },
      { text: "Garlic cloves", qty: "6", dept: "Produce" },
      { text: "White wine", qty: "1/2 cup", dept: "Pantry" },
      { text: "Lemon", qty: "1", dept: "Produce" },
      { text: "Fresh parsley", qty: "1/4 cup", dept: "Produce" },
      { text: "Red pepper flakes", qty: "pinch", dept: "Pantry" },
    ],
    steps: [
      "Cook linguine to al dente.",
      "Melt butter, sauté garlic 1 min.",
      "Add shrimp, cook until pink 3 min.",
      "Pour in wine, lemon juice, simmer 2 min.",
      "Toss with pasta and parsley.",
    ],
  },
];
